import { MutationCache, QueryCache } from '@tanstack/react-query';

import { MutationKey, MutationMethod, QueryPrefix, queryClient } from '@/queryClient';

type QueriesWithoutLoadingAnnouncement =
  | QueryPrefix.summaries
  | QueryPrefix.assignmentQuestions
  | QueryPrefix.assignmentGrades
  | QueryPrefix.accessCodes
  | QueryPrefix.myMessages
  | QueryPrefix.lectureNotes
  | QueryPrefix.threads
  | QueryPrefix.assignmentAttempts
  | QueryPrefix.myUnsavedPrompt
  | QueryPrefix.enrollment
  | QueryPrefix.studentRequestConfigs
  | QueryPrefix.adoptionActionTerms
  | QueryPrefix.courseSectionsForCopyAssignments
  | QueryPrefix.courseSectionAdoptionIsbn;

type MutationsWithoutLoadingAnnouncement = `${QueriesWithoutLoadingAnnouncement}-${MutationMethod}`;

export type MutationLoaderKey = Exclude<MutationKey, MutationsWithoutLoadingAnnouncement>;

export type QueryLoaderKey = Exclude<QueryPrefix, QueriesWithoutLoadingAnnouncement>;

type LoaderEntryKey = MutationLoaderKey | QueryLoaderKey;

interface LoaderEntry {
  key: LoaderEntryKey;
  updatedAt: number;
  isActive: boolean;
}

const entries: Record<string, LoaderEntry> = {};
const listeners = new Set<() => void>();

let notificationPending = false;

const notify = () => {
  for (const l of listeners) l();
};

const scheduleNotify = () => {
  if (notificationPending) {
    return;
  }

  notificationPending = true;
  queueMicrotask(() => {
    notificationPending = false;
    notify();
  });
};

const upsert = (key: LoaderEntryKey, isActive: boolean) => {
  const existing = entries[key];
  if (existing && existing.isActive === isActive) return;
  entries[key] = { key, isActive, updatedAt: Date.now() };
  scheduleNotify();
};

export const subscribeToQueryLoadingAnnouncements = (queryCache: QueryCache) =>
  queryCache.subscribe((event) => {
    const { query } = event;
    if (!query) {
      return;
    }

    const prefix = query.queryKey[0];
    if (!prefix || query.meta?.suppressLoadingAnnouncement || !(prefix satisfies QueryLoaderKey)) {
      return;
    }

    const isActive = queryClient.isFetching({ queryKey: query.queryKey }) > 0;
    upsert(prefix, isActive);
  });

export const subscribeToMutationLoadingAnnouncements = (mutationCache: MutationCache) =>
  mutationCache.subscribe((event) => {
    const { mutation } = event;
    if (!mutation) {
      return;
    }

    const { mutationKey } = mutation.options;
    if (!mutationKey || mutation.meta?.suppressLoadingAnnouncement) {
      return;
    }

    const [subject, method] = mutationKey;
    const composite = `${subject}-${method}` as MutationLoaderKey;

    const isActive = queryClient.isMutating({ mutationKey }) > 0;
    upsert(composite, isActive);
  });

export const getLoadingAnnouncementSnapshot = (
  keyToMessage: Partial<Record<LoaderEntryKey, string>>,
  loadingCompleteMessage: string
): string => {
  const arr = Object.values(entries);
  if (!arr.length) {
    return '';
  }

  const active = arr.filter((e) => e.isActive);
  if (!active.length) {
    return loadingCompleteMessage;
  }

  const latest = active.reduce((a, b) => (b.updatedAt > a.updatedAt ? b : a));

  return keyToMessage[latest.key] || '';
};

export const subscribeLoadingAnnouncementStore = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
